/**
 * @fileoverview Evidence Gallery Component
 * @module EvidenceGallery
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Image, FileText, Download, Trash2, X, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';
import EvidenceThumbnail from './EvidenceThumbnail';

interface EvidenceDocument {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  description?: string;
  uploadedAt: string;
}

interface EvidenceGalleryProps {
  evidence: EvidenceDocument[];
  onDelete: (evidenceId: string) => void;
}

const EvidenceGallery: React.FC<EvidenceGalleryProps> = ({ evidence, onDelete }) => {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const handleDownload = async (evidenceItem: EvidenceDocument) => {
    setDownloading(evidenceItem.id);
    try {
      const response = await apiClient.evidence.getDownloadUrl(evidenceItem.id);
      if (response.data.success) {
        const { downloadUrl } = response.data.data;
        
        // For images, show preview; for others, download
        if (evidenceItem.fileType.startsWith('image/')) {
          setPreviewUrl(downloadUrl);
        } else {
          window.open(downloadUrl, '_blank');
        }
      }
    } catch (error: any) {
      console.error('Download error:', error);
      const msg = error?.response?.data?.error?.message;
      toast.error(msg || 'Failed to download file. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (evidenceId: string) => {
    if (!confirm('Are you sure you want to delete this evidence?')) return;
    
    setDeleting(evidenceId);
    try {
      await apiClient.evidence.delete(evidenceId);
      onDelete(evidenceId);
    } catch (error: any) {
      console.error('Delete error:', error);
      const msg = error?.response?.data?.error?.message;
      toast.error(msg || 'Failed to delete evidence. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return <Image className="w-6 h-6 text-blue-500" />;
    }
    return <FileText className="w-6 h-6 text-gray-500" />;
  };

  if (evidence.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
        <Image className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">No evidence uploaded yet</p>
        <p className="text-gray-500 text-sm mt-1">Upload photos to document this issue</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {evidence.map(item => (
          <div
            key={item.id}
            className="relative group bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
          >
            {/* Thumbnail/Icon */}
            <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
              {item.fileType.startsWith('image/') ? (
                <EvidenceThumbnail
                  evidenceId={item.id}
                  fileName={item.fileName}
                  className="w-full h-full object-cover"
                />
              ) : (
                getFileIcon(item.fileType)
              )}
            </div>

            {/* Info */}
            <div className="p-3">
              <p className="text-sm font-medium text-gray-900 truncate" title={item.fileName}>
                {item.fileName}
              </p>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-gray-500">{formatFileSize(item.fileSize)}</span>
                <span className="text-xs text-gray-500">{formatDate(item.uploadedAt)}</span>
              </div>
            </div>

            {/* Actions Overlay */}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button
                onClick={() => handleDownload(item)}
                disabled={downloading === item.id}
                className="p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
                title="View/Download"
              >
                {downloading === item.id ? (
                  <Loader2 className="w-5 h-5 text-gray-600 animate-spin" />
                ) : (
                  <Download className="w-5 h-5 text-gray-600" />
                )}
              </button>
              <button
                onClick={() => handleDelete(item.id)}
                disabled={deleting === item.id}
                className="p-2 bg-white rounded-lg hover:bg-red-100 transition-colors"
                title="Delete"
              >
                {deleting === item.id ? (
                  <Loader2 className="w-5 h-5 text-red-600 animate-spin" />
                ) : (
                  <Trash2 className="w-5 h-5 text-red-600" />
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Image Preview Modal */}
      {previewUrl && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewUrl(null)}
        >
          <button
            onClick={() => setPreviewUrl(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={previewUrl}
            alt="Evidence preview"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default EvidenceGallery;















